// PlanComponents/ProgressTrackingCard.js
import React from 'react';
import { Typography, Box, CircularProgress, Chip, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import { toManYenLabel } from './utils';

// ステータスチップの色
const statusColorMap = {
  '順調': '#4caf50',
  'やや遅延': '#ff9800',
  '遅延': '#f44336',
  '完了': '#2196f3',
  '未開始': '#9e9e9e',
};

/**
 * 円形プログレスコンポーネント
 */
function CircularMetric({ value, label, color = '#4e7df3', size = 80 }) {
  const theme = useTheme();
  const displayValue = value !== null && value !== undefined ? Math.round(value) : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* 背景の円 */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{ color: 'rgba(0,0,0,0.06)', position: 'absolute' }}
        />
        {/* 実際の値 */}
        <CircularProgress
          variant="determinate"
          value={Math.min(100, displayValue)}
          size={size}
          thickness={4}
          sx={{ color }}
        />
        <Box sx={{
          top: 0, left: 0, bottom: 0, right: 0,
          position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: textColors(theme).primary, fontSize: '0.85rem' }}>
            {value !== null && value !== undefined ? `${displayValue}%` : '—'}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).muted, textAlign: 'center' }}>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * 進捗トラッキングカード
 * 科学的根拠: ISO 30414:2018, マイナビ中途採用状況調査2025年版
 */
export default function ProgressTrackingCard({ progressData }) {
  const theme = useTheme();

  if (!progressData) {
    return (
      <CardItem title="進捗トラッキング">
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>実績データがありません</Typography>
      </CardItem>
    );
  }

  const { metrics, benchmarks } = progressData;
  const statusColor = statusColorMap[metrics?.status] || '#9e9e9e';

  return (
    <CardItem
      title="進捗トラッキング"
      titleExtra={
        <Chip
          label={metrics?.status || '未開始'}
          sx={{ backgroundColor: statusColor, color: '#fff', fontWeight: 700, fontSize: '0.8rem', px: 1.5 }}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* 3つの円形プログレス */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 120, p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularMetric
              value={metrics?.budget_consumption_rate}
              label="予算消化率"
              color={metrics?.budget_consumption_rate > 90 ? '#f44336' : '#4e7df3'}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 120, p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularMetric
              value={metrics?.hiring_achievement_rate}
              label="採用達成率"
              color={metrics?.hiring_achievement_rate >= 100 ? '#4caf50' : '#ff9800'}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 120, p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularMetric
              value={metrics?.period_progress_rate}
              label="期間進捗率"
              color="#26a69a"
            />
          </Box>
        </Box>

        {/* ベンチマーク比較 */}
        {benchmarks && (
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).secondary, mb: 1 }}>
              日本市場ベンチマーク比較
            </Typography>

            {/* 実績CPH vs 日本平均 */}
            {metrics?.actual_cph !== null && metrics?.actual_cph !== undefined && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  実績CPH: {toManYenLabel(metrics.actual_cph)}
                </Typography>
                <Tooltip title={benchmarks.japan_avg_cph_source} arrow>
                  <Typography variant="caption" sx={{ color: textColors(theme).muted, cursor: 'help' }}>
                    日本平均: {benchmarks.japan_avg_cph_label}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {/* 実績歩留まり率 vs 日本平均 */}
            {metrics?.actual_yield_rate !== null && metrics?.actual_yield_rate !== undefined && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  実績歩留まり率: {metrics.actual_yield_rate}%
                </Typography>
                <Tooltip title={benchmarks.japan_avg_yield_source} arrow>
                  <Typography variant="caption" sx={{ color: textColors(theme).muted, cursor: 'help' }}>
                    日本平均: {benchmarks.japan_avg_yield_rate}%
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {/* 採用充足率 vs 日本平均 */}
            {metrics?.hiring_achievement_rate !== null && metrics?.hiring_achievement_rate !== undefined && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  採用達成率: {metrics.hiring_achievement_rate}%
                </Typography>
                <Tooltip title={benchmarks.japan_avg_fulfillment_source} arrow>
                  <Typography variant="caption" sx={{ color: textColors(theme).muted, cursor: 'help' }}>
                    日本平均充足率: {benchmarks.japan_avg_fulfillment_rate}%
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        {/* 出典 */}
        <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic' }}>
          ※ ベンチマークは日本市場調査およびグローバル人事統計に基づく
        </Typography>
      </Box>
    </CardItem>
  );
}
